// Biteship — helper MURNI (tanpa I/O, tanpa React, tanpa Supabase).
//
// Modul ini memegang SATU-SATUNYA definisi dari empat hal yang harus sama
// persis di sisi klien, sisi server, dan di dalam kunci cache DB:
//   1. kunci kecamatan  (`biteshipDistrictKey`)
//   2. bucket berat     (`weightBucketGram`)
//   3. kunci kurir      (`couriersKey`)
//   4. bentuk tarif     (`normalizeRates`)
// Kalau salah satunya disalin ke tempat lain lalu menyimpang, gejalanya bukan
// error melainkan cache yang meleset diam-diam (tarif kecamatan A dijawab untuk
// kecamatan B, atau hit-rate anjlok tanpa ada yang tahu). Rencana lengkap:
// docs/rencana-biteship-ongkir.md §3.
//
// Respons Biteship adalah JSON dari pihak luar → tipe masukannya sengaja
// longgar (`unknown` per-field) dan dinormalkan di sini, supaya perubahan
// bentuk di sisi mereka berakhir sebagai baris yang dibuang, bukan `undefined`
// yang menjalar ke DB.

// ─── Bentuk data ────────────────────────────────────────────────────────────

/** Satu baris `biteship_areas` (mig 3720). */
export type BiteshipAreaRow = {
  area_id: string;
  district_key: string;
  name: string;
  province_name: string;
  city_name: string;
  district_name: string;
  postal_code: string;
};

/** Setelan asal pengiriman sebuah toko — hasil baca key `shipping_*`. */
export type ShippingOriginSettings = {
  enabled: boolean;
  originAreaId: string;
  originDistrictKey: string;
  originLabel: string;
  /** Kode kurir aktif, sudah kanonik (lower, unik, urut). */
  couriers: string[];
  /**
   * Toleransi berat (gram) yang dipotong sebelum pembulatan per-kg, 0–999.
   * 0 = perilaku bawaan. Lihat peringatan di `weightBucketGram`: nilai > 0
   * berarti toko menanggung selisih terhadap tagihan kurir.
   */
  weightToleranceGram: number;
};

/** Satu tarif ternormalisasi — bentuk yang disimpan di `shipping_rate_cache.payload`. */
export type ShippingRate = {
  courierCode: string;
  courierName: string;
  serviceCode: string;
  serviceName: string;
  /** Estimasi lama kirim apa adanya dari Biteship ("1 - 2 days"). "" bila kosong. */
  duration: string;
  /** Total yang dibayar pembeli (rupiah). Selalu > 0 — lihat `normalizeRates`. */
  price: number;
  /**
   * Id kutipan `shipping_quotes` (mig 4020) — ditempelkan route, BUKAN
   * Biteship. Inilah yang dibawa klien saat ongkir digabungkan ke tagihan:
   * nominalnya dibaca server dari barisnya, jadi angka di sini tak pernah
   * menjadi angka yang ditagihkan.
   *
   * Opsional: kutipan gagal terbit = tarif tetap tampil & tetap bisa dipilih,
   * hanya ongkirnya kembali ditetapkan admin (perilaku sebelum mig 4020).
   */
  quoteId?: string;
  /**
   * Rincian per gudang (Multigudang, mig 7200) — HANYA ada pada tarif yang
   * dihitung route saat saklar Multigudang menyala dan layarnya mengirim
   * barang yang dikirim. `price` di atas = jumlah `legs[].price`. Absen = tarif
   * satu asal (perilaku sebelum Multigudang).
   */
  legs?: RateLeg[];
};

/**
 * Satu gudang dalam tarif multigudang (mig 7200). Nominalnya tetap ditagih
 * lewat kutipan server (`shipping_quotes.legs`), bukan dari angka ini.
 */
export type RateLeg = {
  /** `null` = Gudang Utama (lokasi toko). */
  warehouseId: string | null;
  name: string;
  /** Berat barang gudang ini yang dikutip (gram). */
  weightGram: number;
  price: number;
};

/** Bentuk MENTAH satu area dari `GET /v1/maps/areas`. Semua field tak dipercaya. */
export type BiteshipAreaResponseItem = {
  id?: unknown;
  name?: unknown;
  administrative_division_level_1_name?: unknown;
  administrative_division_level_2_name?: unknown;
  administrative_division_level_3_name?: unknown;
  postal_code?: unknown;
};

/** Bentuk MENTAH satu entri `pricing[]` dari `POST /v1/rates/couriers`. */
export type BiteshipPricingItem = {
  courier_code?: unknown;
  courier_name?: unknown;
  courier_service_code?: unknown;
  courier_service_name?: unknown;
  duration?: unknown;
  price?: unknown;
  service_type?: unknown;
};

// ─── Koersi internal ────────────────────────────────────────────────────────

/** String yang aman: hanya `string`/`number` yang diterima, sisanya "". */
function str(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

/** Angka yang aman: null bila bukan angka berhingga. String numerik diterima. */
function num(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// ─── 1. Kunci kecamatan ─────────────────────────────────────────────────────

/**
 * `area_id` Biteship → **district key**, yaitu bagian sebelum segmen
 * `IDZ<kodepos>`:
 *
 *   IDNP6IDNC148IDND843IDZ12250  →  IDNP6IDNC148IDND843
 *   (provinsi, kota, KECAMATAN,   |  kode pos dibuang)
 *
 * Satu kecamatan punya banyak kode pos = banyak `area_id`, tapi district
 * key-nya sama. Itulah yang membuatnya layak jadi kunci cache tarif: kurir
 * reguler menagih sama untuk seluruh kecamatan.
 *
 * ⚠️ FALLBACK WAJIB. Format ini TIDAK dijamin dokumentasi Biteship. Kalau pola
 * `IDZ\d+` tidak ketemu — atau ketemu tapi menyisakan awalan KOSONG — fungsi
 * mengembalikan `areaId` apa adanya. Konsekuensinya cache jadi lebih terpecah
 * (satu entri per kode pos, bukan per kecamatan) = kurang efisien, TIDAK PERNAH
 * salah. Kembali "" untuk kunci yang kosong akan jauh lebih buruk: semua area
 * yang tak terurai akan bertabrakan di satu baris cache.
 *
 * Pencocokan sengaja CASE-SENSITIVE: `area_id` adalah kode, selalu huruf besar.
 * Melonggarkannya berisiko memotong di tempat yang salah.
 */
export function biteshipDistrictKey(areaId: string | null | undefined): string {
  const id = str(areaId);
  if (id === "") return "";
  const cut = id.search(/IDZ\d+/);
  if (cut <= 0) return id; // tak ketemu (-1) atau awalan kosong (0) → utuh
  return id.slice(0, cut);
}

// ─── 2. Bucket berat ────────────────────────────────────────────────────────

/** Berat minimum yang ditagih kurir: 1 kg. */
export const MIN_WEIGHT_BUCKET_GRAM = 1000;

/**
 * Batas atas berat yang masuk akal untuk satu paket: 1 ton.
 *
 * BUKAN aturan bisnis — pagar INTEGRITAS. `shipping_rate_cache
 * .weight_bucket_gram` bertipe `integer` (int4, maks 2.147.483.647), jadi berat
 * di atas itu menghasilkan bucket yang **gagal di-INSERT**. Kegagalannya senyap
 * (`putCachedRates` best-effort, errornya cuma di-log), sehingga akibatnya
 * bukan error melainkan **cache miss permanen**: tiap permintaan dengan berat
 * itu memukul Biteship lagi atas kuota platform.
 *
 * Ditegakkan sebagai PENOLAKAN di route, bukan sebagai clamp di
 * `weightBucketGram` — clamp akan melanggar invarian "hasil tidak pernah lebih
 * kecil dari berat sebenarnya" dan berubah jadi under-quote diam-diam, persis
 * jenis kesalahan yang paling mahal di kolom uang.
 */
export const MAX_SHIPMENT_WEIGHT_GRAM = 1_000_000;

/**
 * Batas atas toleransi berat: 999 g.
 *
 * ≥1000 g berarti menggeser satu kilo PENUH — itu bukan "toleransi" lagi
 * melainkan potongan satu bucket, dan tarif yang ditampilkan akan meleset
 * sekurangnya satu kilo pada SETIAP paket. Dijepit, bukan ditolak: nilai ini
 * datang dari kolom setelan, dan menolaknya berarti route melempar untuk
 * konfigurasi yang bisa diperbaiki admin — sementara menjepitnya tetap
 * memulangkan tarif yang masuk akal.
 */
export const MAX_WEIGHT_TOLERANCE_GRAM = 999;

/**
 * Toleransi berat mentah → bilangan bulat 0–999.
 *
 * SATU definisi untuk semua pembaca (`weightBucketGram`, parser setelan, kelak
 * layar admin). Nilai tak terbaca → 0, yaitu perilaku tanpa toleransi: arah
 * gagal yang benar, karena toleransi yang tak sengaja menyala berarti toko
 * menyubsidi ongkir tanpa memutuskannya.
 */
export function clampWeightToleranceGram(
  value: number | null | undefined
): number {
  const n = num(value);
  if (n === null) return 0;
  return Math.min(MAX_WEIGHT_TOLERANCE_GRAM, Math.max(0, Math.trunc(n)));
}

/**
 * Bulatkan berat KE ATAS per 1 kg, minimum 1 kg.
 *
 *   0 → 1000 · 1 → 1000 · 999 → 1000 · 1000 → 1000 · 1001 → 2000
 *
 * Ini BUKAN pembulatan kasar demi hit-rate cache: kurir Indonesia memang
 * menagih per kg dibulatkan ke atas, jadi bucket ini persis berat yang
 * ditagihkan. Sifat yang dijaga: hasilnya tidak pernah LEBIH KECIL dari berat
 * sebenarnya, sehingga tarif yang ditampilkan tidak pernah *under-quote*.
 *
 * Masukan tak masuk akal (NaN, negatif, Infinity) jatuh ke minimum — arah yang
 * sama: lebih baik menagih 1 kg daripada memancing tarif untuk 0 gram.
 *
 * ─── `toleranceGram` (mig 3760) ──────────────────────────────────────────────
 *
 * Toleransi dipotong dari berat SEBELUM dibulatkan, jadi ia berlaku di SETIAP
 * batas kilo, bukan hanya kilo pertama. Dengan toleransi 250 g:
 *
 *   900 → 1 kg · 1000 → 1 kg · **1250 → 1 kg** · 1251 → 2 kg · **2250 → 2 kg**
 *
 * ⚠️ INI SATU-SATUNYA JALUR YANG SENGAJA MELANGGAR "tidak pernah under-quote"
 * di atas, dan itu memang gunanya. Kurir tetap menagih 1250 g sebagai 2 kg;
 * yang berubah hanya angka yang DILIHAT pembeli. **Selisihnya ditanggung
 * toko** — bukan hilang. Karena itu bawaannya `0` (perilaku lama persis) dan
 * layar setelan WAJIB menuliskan konsekuensinya dengan angka nyata sebelum
 * admin menyalakannya (rencana §14d).
 *
 * Parameter berdefault `0` juga menjaga seluruh pemanggil & tes lama tetap
 * benar tanpa disentuh.
 *
 * Toleransi tidak pernah menurunkan hasil di bawah 1 kg: berat yang lebih kecil
 * dari toleransinya berakhir di minimum, sama seperti berat nol.
 */
export function weightBucketGram(
  gram: number | null | undefined,
  toleranceGram: number | null | undefined = 0
): number {
  const n = num(gram);
  if (n === null || n <= 0) return MIN_WEIGHT_BUCKET_GRAM;
  const tolerated = Math.max(0, n - clampWeightToleranceGram(toleranceGram));
  return Math.max(MIN_WEIGHT_BUCKET_GRAM, Math.ceil(tolerated / 1000) * 1000);
}

// ─── 3. Kunci kurir ─────────────────────────────────────────────────────────

/**
 * Daftar kode kurir → satu string kanonik: lowercase, dibuang yang kosong,
 * unik, URUT abjad, dipisah koma.
 *
 * Dipakai untuk DUA hal sekaligus, dan itu disengaja:
 *   • `shipping_rate_cache.couriers_key` (bagian dari unique 4 kolom), dan
 *   • parameter `couriers` yang dikirim ke Biteship.
 * Karena keduanya string yang sama, kunci cache mustahil menggambarkan daftar
 * kurir yang berbeda dari yang benar-benar diminta. Urut + unik membuat
 * ["jnt","jne"] dan ["JNE","jnt","jne"] mendarat di baris cache yang sama.
 */
export function couriersKey(
  codes: ReadonlyArray<string | null | undefined>
): string {
  const seen = new Set<string>();
  for (const raw of codes) {
    const code = str(raw).toLowerCase();
    if (code !== "") seen.add(code);
  }
  return Array.from(seen).sort().join(",");
}

// ─── 4. Normalisasi tarif ───────────────────────────────────────────────────

/**
 * `service_type` Biteship yang TIDAK BOLEH masuk cache tarif. Ini soal
 * KEBENARAN KUNCI CACHE, bukan soal produk — bacalah sampai habis sebelum
 * "sekadar" menambah nilai ke daftar ini.
 *
 * Kunci cache kita adalah `(kecamatan asal, kecamatan tujuan, bucket kg,
 * daftar kurir)` — benar untuk layanan yang ditagih PER-KG (reguler/kargo),
 * karena kurir memang menagih sama untuk satu kecamatan. Layanan `instant`
 * dan `same_day` ditagih berbeda: harganya bergantung JARAK TITIK-KE-TITIK,
 * jadi bisa berbeda antar kode pos DI DALAM satu kecamatan yang sama. Kunci
 * kecamatan tak pernah bisa membedakan itu.
 *
 * Bukan risiko teoretis: probe ke katalog Biteship nyata (2026-08-17, lihat
 * `docs/rencana-biteship-ongkir.md` §11 butir 2 & §17c) menunjukkan 12 dari
 * 22 kurir punya layanan begini — dan DUA di antaranya, `tiki` & `anteraja`,
 * ADA di daftar kurir default `shipping_couriers` (mig 3720). Begitu ada toko
 * yang mengirim di dalam kotanya sendiri, satu harga same-day untuk satu kode
 * pos akan tersaji 24 jam untuk SELURUH kode pos lain di kecamatan itu — ke
 * SEMUA tenant, karena cache ini global lintas-tenant (`rateCacheTtlMs`).
 *
 * `overnight` SENGAJA TIDAK ada di daftar ini. Ia ditagih per-kg dan aman
 * di-cache dengan kunci yang ada — JNE "Yakin Esok Sampai (YES)" & TIKI
 * "One Night Service" muncul normal pada probe rute lintas kota (§17b, Rp
 * 54.000 & Rp 18.000). Membuangnya juga akan menghapus layanan yang benar.
 *
 * Kalau suatu hari `instant`/`same_day` memang mau dijual, syaratnya kunci
 * cache untuk layanan itu turun ke `area_id` PENUH + TTL pendek — pekerjaan
 * tersendiri, bukan sekadar mematikan filter ini.
 */
export const UNSUPPORTED_SERVICE_TYPES: readonly string[] = [
  "instant",
  "same_day"
];

/**
 * `true` bila `service_type` ini AMAN disimpan di cache tarif berkunci-
 * kecamatan (`false` = harganya bergantung jarak titik-ke-titik, lihat
 * `UNSUPPORTED_SERVICE_TYPES`).
 *
 * Kosong / `undefined` → `true` (default AMAN, BUKAN buang). Field ini dari
 * pihak luar dan tak dijamin selalu terisi; membuang tarif yang statusnya tak
 * diketahui lebih mahal daripada risikonya — mayoritas mutlak katalog memang
 * murni per-kg (§17c: 10 kurir murni reguler/kargo, vs 12 yang instant/
 * same_day, dan 2 di antaranya melayani KEDUANYA).
 *
 * Case-insensitive & trim spasi tepi — `service_type` adalah teks dari respons
 * JSON pihak luar, sama seperti field lain yang dilewatkan `str()` di modul
 * ini.
 */
export function isCacheableServiceType(
  serviceType: string | null | undefined
): boolean {
  const type = str(serviceType).toLowerCase();
  if (type === "") return true;
  return !UNSUPPORTED_SERVICE_TYPES.includes(type);
}

/**
 * `pricing[]` mentah → daftar `ShippingRate` yang urut harga NAIK.
 *
 * Tiga jenis baris DIBUANG:
 *
 *  • **Tanpa harga yang sah** (`price` hilang / bukan angka / ≤ 0). Rp 0 dari
 *    API tarif berarti "tak ada quote", bukan "gratis" — dan menampilkannya
 *    sebagai pilihan yang bisa diklik berarti ongkir Rp 0 masuk ke nota. Kalau
 *    suatu hari Biteship benar-benar memulangkan tarif gratis, akibat terburuk
 *    aturan ini hanyalah pilihan itu tak muncul dan admin mengetik 0 sendiri —
 *    jauh lebih murah daripada kebalikannya.
 *  • **Tanpa `courier_code`.** Tarif yang tak bisa diidentifikasi juga tak bisa
 *    dicatat sebagai ekspedisi di `shipment_requests`, jadi ia cuma pilihan
 *    yang menyesatkan.
 *  • **`service_type` tak layak-cache** (`instant`/`same_day`, lihat
 *    `isCacheableServiceType`) — soal KUNCI CACHE, bukan harga: nilainya
 *    sendiri sah, tapi kecamatan bukan kunci yang benar untuknya. Kalau
 *    `pricing[]` isinya HANYA layanan begini, hasilnya `[]` — itu keadaan sah
 *    (rute tanpa opsi yang aman di-cache per-kecamatan), bukan error, dan
 *    lapisan cache di atas fungsi ini sudah menanganinya lewat TTL pendek
 *    (`rateCacheTtlMs`).
 *
 * Urutan: harga naik, lalu `courierCode` & `serviceCode` sebagai tie-break.
 * Tie-break-nya bukan kosmetik — payload ini disimpan ke cache dan
 * dibandingkan di tes; urutan yang bergantung pada urutan balasan API membuat
 * dua panggilan identik menghasilkan dua payload berbeda.
 */
export function normalizeRates(
  pricing: ReadonlyArray<BiteshipPricingItem> | null | undefined
): ShippingRate[] {
  if (!Array.isArray(pricing)) return [];
  const rates: ShippingRate[] = [];

  for (const item of pricing) {
    if (!item || typeof item !== "object") continue;
    const courierCode = str(item.courier_code).toLowerCase();
    if (courierCode === "") continue;
    const price = num(item.price);
    if (price === null || price <= 0) continue;
    if (!isCacheableServiceType(str(item.service_type))) continue;

    rates.push({
      courierCode,
      courierName: str(item.courier_name),
      serviceCode: str(item.courier_service_code).toLowerCase(),
      serviceName: str(item.courier_service_name),
      duration: str(item.duration),
      price: Math.round(price),
    });
  }

  rates.sort(
    (a, b) =>
      a.price - b.price ||
      a.courierCode.localeCompare(b.courierCode) ||
      a.serviceCode.localeCompare(b.serviceCode)
  );
  return rates;
}

// ─── 5. Baris area ──────────────────────────────────────────────────────────

/**
 * Satu entri `areas[]` mentah → baris `biteship_areas`, atau `null` bila tak
 * bisa dipakai.
 *
 * `area_id` adalah primary key tabelnya DAN asal district key, jadi baris tanpa
 * `id` tidak punya identitas apa pun — dibuang, bukan disimpan dengan id kosong
 * (satu baris "" yang saling menimpa jauh lebih membingungkan daripada baris
 * yang tak ada).
 *
 * `name` boleh kosong dari API; kalau begitu, ia disusun dari komponen
 * administratifnya supaya autocomplete tetap punya sesuatu untuk ditampilkan
 * dan index trigram tetap ada isinya.
 */
export function parseAreaRow(
  area: BiteshipAreaResponseItem | null | undefined
): BiteshipAreaRow | null {
  if (!area || typeof area !== "object") return null;
  const areaId = str(area.id);
  if (areaId === "") return null;

  const province = str(area.administrative_division_level_1_name);
  const city = str(area.administrative_division_level_2_name);
  const district = str(area.administrative_division_level_3_name);
  const postal = str(area.postal_code);

  const name =
    str(area.name) ||
    [district, city, province].filter((part) => part !== "").join(", ");

  return {
    area_id: areaId,
    district_key: biteshipDistrictKey(areaId),
    name,
    province_name: province,
    city_name: city,
    district_name: district,
    postal_code: postal,
  };
}

// ─── 6. Kata kunci pencarian area ───────────────────────────────────────────

/**
 * Panjang minimum kata kunci sebelum Biteship boleh dipanggil. Aturan PRODUK
 * (hemat kuota), bukan aturan protokol — karena itu ia hidup di modul murni
 * dan dibaca route, bukan ditanam di klien HTTP.
 */
export const MIN_AREA_QUERY_LENGTH = 3;

/**
 * Kata kunci pencarian → bentuk kanonik: lowercase, spasi tepi dibuang, spasi
 * ganda dirapatkan.
 *
 * Bentuk ini dipakai sebagai **primary key `biteship_area_lookups.q`**, jadi
 * ia harus dihitung dengan cara yang persis sama di mana pun. "Pesanggrahan ",
 * "  pesanggrahan" dan "Pesanggrahan" wajib mendarat di satu baris — kalau
 * tidak, gerbang "sudah pernah diambil dalam 30 hari" tak pernah kena dan tiap
 * variasi ketikan memukul Biteship lagi.
 */
export function normalizeAreaQuery(raw: string | null | undefined): string {
  return str(raw).toLowerCase().replace(/\s+/g, " ").slice(0, MAX_AREA_QUERY_LENGTH);
}

/**
 * Batas panjang kata kunci — pagar INTEGRITAS, bukan aturan produk.
 *
 * Hasil `normalizeAreaQuery` adalah **primary key `biteship_area_lookups.q`**,
 * dan barisnya ditulis atas kata kunci yang sepenuhnya ditentukan pemanggil.
 * Tanpa batas, satu akun yang login bisa menanam baris sebesar apa pun (dan
 * sebanyak jatah rate-limit-nya) di tabel GLOBAL yang dipakai semua tenant.
 *
 * 64 karakter jauh di atas nama kecamatan terpanjang di Indonesia, jadi
 * pencarian sungguhan tak pernah menyentuhnya. Dua kata kunci yang baru berbeda
 * SETELAH karakter ke-64 akan berbagi satu baris — konsekuensi yang diterima:
 * keduanya toh menghasilkan pencarian yang sama-sama tak masuk akal.
 */
export const MAX_AREA_QUERY_LENGTH = 64;

/**
 * Kata penanda TINGKAT WILAYAH yang dibuang sebelum pencocokan.
 *
 * `biteship_areas.name` berbentuk `"<Kecamatan>, <Kota>, <Provinsi>. <Kodepos>"`
 * dan TIDAK PERNAH memuat kata-kata ini — diverifikasi di PROD 2026-09-04: nol
 * dari 3.685 baris memuatnya sebagai penanda tingkat. Jadi orang yang mengetik
 * "kota tangerang" atau "kec pasar kemis" sedang menyebut sesuatu yang mustahil
 * ada di kolomnya, dan meng-AND-kannya menjamin NOL hasil untuk ketikan yang
 * justru paling wajar.
 *
 * Yang dibuang hanya token BERDIRI SENDIRI. "Kota Baru" (kecamatan sungguhan di
 * Jambi) menyisakan "baru" dan tetap ketemu — superset, sifat yang sama dengan
 * penggantian metakarakter di bawah.
 */
const AREA_LEVEL_WORDS = new Set([
  "kec",
  "kecamatan",
  "kab",
  "kabupaten",
  "kota",
  "kel",
  "kelurahan",
  "desa",
  "prov",
  "provinsi",
]);

/**
 * Satu token → pola ILIKE `%…%`.
 *
 * Karakter yang punya makna khusus di pola LIKE (`%`, `_`) atau di parser
 * filter PostgREST (`,` `.` `(` `)` `:` `"` `\`) diganti `_` = wildcard SATU
 * karakter — idiom `stockSearchPattern` (stock.ts). Menggantinya dengan
 * wildcard, bukan membuangnya, menjaga sifat SUPERSET: `_` cocok dengan
 * karakter aslinya maupun karakter lain, jadi tak ada baris yang hilang.
 */
function areaTokenPattern(token: string): string {
  return `%${token.replace(/[%_,.():"\\]/g, "_")}%`;
}

/**
 * Satu percobaan pencarian: pola-pola yang harus cocok SEMUA, plus frasa yang
 * benar-benar dicari supaya layar bisa mengatakannya.
 */
export type AreaSearchAttempt = {
  /** Pola ILIKE, di-AND. */
  terms: string[];
  /** Frasa ternormalisasi yang diwakili percobaan ini ("jakarta timur"). */
  label: string;
  /** `true` bila token kiri sudah dibuang — bukan lagi kata kunci utuh. */
  relaxed: boolean;
};

/** Token → pola, dedupe, urutan dipertahankan. */
function attemptFrom(tokens: string[], relaxed: boolean): AreaSearchAttempt {
  const unique = [...new Set(tokens)];
  return {
    terms: unique.map(areaTokenPattern),
    label: unique.join(" "),
    relaxed,
  };
}

/**
 * Kata kunci → TANGGA percobaan, dari paling spesifik ke paling longgar.
 *
 * Anak tangga 0 memakai seluruh token; tiap anak tangga berikutnya membuang
 * satu token dari KIRI. Pemanggil mengambil yang PERTAMA berhasil.
 *
 * ── KENAPA DARI KIRI ────────────────────────────────────────────────────────
 * Orang Indonesia mengetik alamat dari SPESIFIK ke UMUM — "pondok kopi jakarta
 * timur", "tritih kulon cilacap", "condong campur pejawaran banjarnegara".
 * Token paling kiri itu kelurahan/desa/jalan, sementara `biteship_areas.name`
 * hanya memuat nama KECAMATAN ke atas. Meng-AND-kan semuanya membuat satu
 * token yang lebih spesifik dari kecamatan mematikan seluruh pencarian.
 *
 * Diukur di PROD 2026-09-04: dari 1.046 kata kunci yang menjawab nol, **609
 * pulih** begitu token kiri dibuang bertahap. Itu 58% jalan buntu yang selama
 * ini tak punya sebab yang bisa dijelaskan ke pemakainya.
 *
 * ── KENAPA TIAP ANAK TANGGA DIUJI ULANG PANJANGNYA ──────────────────────────
 * `MIN_AREA_QUERY_LENGTH` dulu hanya diuji pada kata kunci UTUH, dan itu bocor
 * begitu penanda tingkat dibuang: "kota t" (6 huruf, lolos gerbang) menyusut
 * jadi token "t" dan mencocokkan 3.440 baris — padahal sebelum pemecahan token
 * ia nol. "kota t"/"kota ta" bukan ketikan aneh: keduanya dilewati saat orang
 * mengetik "kota tangerang" huruf demi huruf, dan dengan debounce 400 ms
 * keadaan antara itu benar-benar menembak.
 *
 * Aturannya: sebuah percobaan sah hanya bila memuat SEKURANGNYA SATU token
 * sepanjang `MIN_AREA_QUERY_LENGTH`. Token pendek di samping token panjang
 * tetap boleh — ia cuma MEMPERSEMPIT ("ciputat ti").
 */
export function areaSearchAttempts(
  raw: string | null | undefined
): AreaSearchAttempt[] {
  const q = normalizeAreaQuery(raw);
  if (q.length < MIN_AREA_QUERY_LENGTH) return [];

  const tokens = q.split(" ").filter((token) => token !== "");
  const kept = tokens.filter((token) => !AREA_LEVEL_WORDS.has(token));

  // Semua token ternyata penanda tingkat ("kota kabupaten") → pakai token
  // aslinya. Memulangkan `[]` di sini berarti "tanpa filter" bagi pemanggil,
  // dan query tanpa filter memulangkan baris sembarang.
  const used = kept.length > 0 ? kept : tokens;

  const attempts: AreaSearchAttempt[] = [];
  for (let drop = 0; drop < used.length; drop += 1) {
    const slice = used.slice(drop);
    if (!slice.some((token) => token.length >= MIN_AREA_QUERY_LENGTH)) continue;
    attempts.push(attemptFrom(slice, drop > 0));
  }
  return attempts;
}

/**
 * Pola untuk percobaan PALING SPESIFIK saja — kata kunci apa adanya.
 *
 * Inilah yang dicerminkan sisi SQL (`search_public_areas`); pelonggaran
 * bertahap sengaja hidup di TypeScript saja, karena ia butuh melihat JUMLAH
 * hasil tiap anak tangga untuk memutuskan (lihat `searchAreasLocal`).
 */
export function areaSearchTerms(raw: string | null | undefined): string[] {
  return areaSearchAttempts(raw)[0]?.terms ?? [];
}


/**
 * Batas baris yang diambil dari DB sebelum diperingkat, dan batas yang benar-
 * benar dikirim ke picker.
 *
 * Keduanya dipisah karena inilah bug kedua yang diperbaiki bersama ini.
 * Sebelumnya DB langsung di-`limit(20)` dengan `order by name asc` — dan
 * "tangerang" cocok 86 baris di PROD, sementara **Kecamatan Tangerang sendiri
 * ada di urutan 81–84**. Pengguna mengetik "tangerang", melihat Balaraja sampai
 * Curug, lalu menyimpulkan kecamatannya tidak terdaftar. Ikut tak terlihat: 24
 * kecamatan lain (Pasar Kemis, Periuk, Pinang, Karawaci, Tigaraksa …) dan
 * SELURUH Tangerang Selatan.
 *
 * Memotong SETELAH memberi peringkat memindahkan yang paling mungkin dimaksud
 * ke atas; memotong sebelum itu (yang lama) memotong menurut abjad, yang tak
 * ada hubungannya dengan apa yang dicari orang.
 */
export const AREA_RANK_FETCH_LIMIT = 200;

/** Baris `biteship_areas` yang cukup untuk diperingkat — sengaja longgar. */
type RankableArea = {
  name: string;
  district_name: string;
};

/**
 * Skor satu baris terhadap kata kunci ASLI (sudah ternormalisasi, bukan pola).
 * Makin kecil makin dulu tampil.
 *
 * Tiga tingkat, dan urutannya menjawab satu pertanyaan: dari semua baris yang
 * cocok, mana yang paling mungkin DIMAKSUD pengetiknya?
 *
 *   0 — `district_name` sama persis. "tangerang" → "Tangerang, Tangerang".
 *   1 — `district_name` berawalan kata kunci. "cipu" → "Ciputat", "Ciputat Timur".
 *   2 — `district_name` mengandungnya di tengah. "timur" → "Ciputat Timur".
 *   3 — kecamatannya tak menyebutnya sama sekali; yang cocok kota/provinsinya.
 *
 * Tingkat 3 itu mayoritas hasil untuk kata kunci selebar "tangerang" — dan
 * justru karena ia mayoritas, satu-satunya baris bertingkat 0 tenggelam tanpa
 * peringkat ini.
 *
 * Kata kunci ber-spasi dicocokkan UTUH di sini, bukan per token: "ciputat
 * timur" harus menang atas "Ciputat", dan token yang dipecah-pecah justru
 * membuat keduanya seri. Penyempitan per-token sudah dikerjakan
 * `areaSearchTerms` di sisi query — di sini tugasnya tinggal mengurutkan.
 */
function areaRank(row: RankableArea, query: string): number {
  const district = row.district_name.trim().toLowerCase();
  if (district === "") return 3;
  if (district === query) return 0;
  if (district.startsWith(query)) return 1;
  if (district.includes(query)) return 2;
  return 3;
}

/**
 * Urutkan hasil pencarian kecamatan menurut relevansi, lalu `name` ASC.
 *
 * MURNI dan STABIL: tidak menyentuh array masukan (`slice()` dulu), dan baris
 * berperingkat sama selalu keluar dalam urutan `name` yang sama — dua sifat
 * yang diperlukan supaya dua ketikan identik tak pernah menghasilkan daftar
 * yang berbeda urutan.
 *
 * `name` ASC sebagai tiebreak, bukan kode pos numerik: satu kecamatan punya
 * banyak kode pos dan `name` sudah memuat kode posnya di ekor, jadi urutannya
 * otomatis mengelompok per kecamatan lalu menaik per kode pos.
 */
export function rankAreaMatches<T extends RankableArea>(
  rows: readonly T[],
  rawQuery: string | null | undefined
): T[] {
  const query = normalizeAreaQuery(rawQuery);
  return rows
    .slice()
    .sort(
      (a, b) =>
        areaRank(a, query) - areaRank(b, query) || a.name.localeCompare(b.name)
    );
}
// ─── 7. Setelan asal pengiriman (site_settings) ─────────────────────────────

/** Key `site_settings` milik fitur ini (lima dari mig 3720, satu dari mig 3760). */
export const SHIPPING_SETTING_KEYS = {
  enabled: "shipping_rate_enabled",
  originAreaId: "shipping_origin_area_id",
  originDistrictKey: "shipping_origin_district_key",
  originLabel: "shipping_origin_label",
  couriers: "shipping_couriers",
  weightToleranceGram: "shipping_weight_tolerance_gram",
} as const;

export const SHIPPING_SETTING_KEY_LIST: string[] = Object.values(
  SHIPPING_SETTING_KEYS
);

/**
 * Baris `site_settings` → `ShippingOriginSettings`.
 *
 * `enabled` memakai konvensi **default MATI** yang sama dengan
 * `dropship_fee_enabled` di `mapSettingsRows`
 * (siteSettings.ts): hanya token "hidup" eksplisit yang menyalakan. Daftar
 * tokennya sengaja identik — Fase 2/5b menambahkan keenam key ini ke
 * `mapSettingsRows` untuk layar admin, dan dua pembaca yang tidak sepakat soal
 * arti "1" akan membuat kartu setelan menunjukkan NYALA sementara route
 * menjawab `{enabled:false}`.
 *
 * `originDistrictKey` boleh kosong di DB (mis. disunting tangan lewat SQL) →
 * diturunkan dari `originAreaId` supaya kunci cache tetap terbentuk.
 */
export function parseShippingOriginSettings(
  rows: ReadonlyArray<{ key: string; value: string | null }>
): ShippingOriginSettings {
  const map = new Map<string, string>();
  for (const row of rows) {
    if (row && typeof row.key === "string") map.set(row.key, str(row.value));
  }

  const enabledRaw = map.get(SHIPPING_SETTING_KEYS.enabled);
  const enabled =
    enabledRaw === undefined
      ? false
      : ["true", "1", "on", "yes"].includes(enabledRaw.trim().toLowerCase());

  const originAreaId = map.get(SHIPPING_SETTING_KEYS.originAreaId) ?? "";
  const storedKey = map.get(SHIPPING_SETTING_KEYS.originDistrictKey) ?? "";

  return {
    enabled,
    originAreaId,
    originDistrictKey: storedKey || biteshipDistrictKey(originAreaId),
    originLabel: map.get(SHIPPING_SETTING_KEYS.originLabel) ?? "",
    couriers: (map.get(SHIPPING_SETTING_KEYS.couriers) ?? "")
      .split(",")
      .map((code) => code.trim().toLowerCase())
      .filter((code) => code !== ""),
    // Baris tak ada / tak terbaca → 0 = tanpa toleransi (perilaku sebelum
    // mig 3760). `clampWeightToleranceGram` yang memutuskan, bukan parser di
    // layar admin — kalau keduanya menjepit berbeda, admin melihat "250 g"
    // sementara route menghitung dengan angka lain.
    weightToleranceGram: clampWeightToleranceGram(
      Number(map.get(SHIPPING_SETTING_KEYS.weightToleranceGram))
    ),
  };
}

// ─── 8. Umur cache tarif ────────────────────────────────────────────────────

/** Umur cache tarif yang BERISI. Cukup pendek agar perubahan tarif menyusul dalam sehari. */
export const RATE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/** Umur cache tarif KOSONG — jauh lebih pendek. Alasannya di `rateCacheTtlMs`. */
export const EMPTY_RATE_CACHE_TTL_MS = 30 * 60 * 1000;

/**
 * Berapa lama satu hasil tarif boleh disimpan.
 *
 * **Daftar kosong TIDAK selalu fakta rute.** Kurir bisa tumbang sementara dan
 * tetap membalas HTTP 200 dengan `pricing: []`; dari sisi kita, "rute ini
 * memang tak dilayani" dan "kurirnya sedang sakit" terlihat persis sama. Karena
 * `shipping_rate_cache` **global lintas tenant** (satu key platform, mig 3720),
 * satu balasan kosong yang di-cache 24 jam akan mematikan pasangan kecamatan
 * itu untuk **SEMUA toko** selama sehari penuh — padahal gangguannya mungkin
 * cuma lima menit.
 *
 * Karena itu hasil kosong dapat TTL sendiri yang jauh lebih pendek: tetap
 * menahan hammering (rute mati tak memukul Biteship di tiap pembukaan halaman),
 * tapi sembuh sendiri dalam setengah jam.
 *
 * Yang SENGAJA tidak dilakukan: menolak menyimpan hasil kosong sama sekali.
 * Rute yang benar-benar tak dilayani ada dan permanen; tanpa entri cache, tiap
 * pembeli yang membuka halaman itu memukul Biteship lagi atas kuota kita.
 *
 * Ini bukan pelanggaran aturan "kegagalan jangan di-cache" — yang disimpan di
 * sini adalah JAWABAN Biteship (kosong), bukan kegagalannya. Kegagalan tak
 * pernah sampai ke fungsi ini: route melempar sebelum menulis apa pun.
 *
 * Asimetri ini disengaja dan arahnya selalu aman: salah menebak "kosong itu
 * fakta" hanya membuat kita memanggil Biteship sedikit lebih sering.
 */
export function rateCacheTtlMs(rates: ReadonlyArray<unknown> | null | undefined): number {
  return Array.isArray(rates) && rates.length > 0
    ? RATE_CACHE_TTL_MS
    : EMPTY_RATE_CACHE_TTL_MS;
}

// ─── 9. Katalog kurir Biteship ──────────────────────────────────────────────

/**
 * Kurir yang bisa dipilih admin — kode Biteship + label tampilan.
 *
 * Sengaja KONSTANTA, bukan hasil `GET /v1/couriers`: daftarnya jarang berubah,
 * dan menjadikannya panggilan jaringan berarti layar setelan bisa gagal dimuat
 * (atau memakan kuota) hanya untuk mengisi sebuah dropdown. Kalau Biteship
 * menambah kurir, tambahkan barisnya di sini.
 *
 * `code` adalah SATU-SATUNYA hal yang dikirim ke Biteship maupun disimpan
 * (`site_settings.shipping_couriers`, `expeditions.biteship_courier_code`);
 * `name` murni tampilan dan boleh diubah tanpa efek apa pun.
 *
 * Urutannya = urutan tampil di layar: yang paling lazim dipakai toko Indonesia
 * lebih dulu, bukan abjad — daftar abjad memaksa admin memindai seluruhnya
 * untuk menemukan JNE.
 */
export const BITESHIP_COURIERS: ReadonlyArray<{ code: string; name: string }> = [
  { code: "jne", name: "JNE" },
  { code: "jnt", name: "J&T Express" },
  { code: "sicepat", name: "SiCepat" },
  { code: "anteraja", name: "AnterAja" },
  { code: "ninja", name: "Ninja Xpress" },
  { code: "pos", name: "POS Indonesia" },
  { code: "tiki", name: "TIKI" },
  { code: "idexpress", name: "ID Express" },
  { code: "sap", name: "SAP Express" },
  { code: "lion", name: "Lion Parcel" },
  { code: "wahana", name: "Wahana" },
  { code: "rpx", name: "RPX" },
  { code: "paxel", name: "Paxel" },
  { code: "gojek", name: "Gojek (instant/same day)" },
  { code: "grab", name: "Grab (instant/same day)" },
] as const;

/** Kode → label tampilan; kode tak dikenal dipulangkan apa adanya (uppercase). */
export function courierLabel(code: string | null | undefined): string {
  const key = str(code).toLowerCase();
  if (key === "") return "";
  return BITESHIP_COURIERS.find((c) => c.code === key)?.name ?? key.toUpperCase();
}

// ─── 9a. Endpoint Biteship yang dicacah (mig 3760) ──────────────────────────

/**
 * Tiga endpoint yang benar-benar dipukul fitur ini — cermin CHECK
 * `biteship_api_usage.endpoint`.
 *
 * Didefinisikan di modul MURNI supaya klien HTTP (yang mencatat 429) dan
 * lapisan DB (yang menulis barisnya) memakai daftar yang sama. Nilai di luar
 * daftar ini diabaikan diam-diam oleh RPC-nya — pencatatan statistik tidak
 * boleh menggagalkan permintaan ongkir yang sudah berhasil.
 */
export const BITESHIP_ENDPOINTS = ["areas", "rates", "couriers"] as const;

export type BiteshipEndpoint = (typeof BITESHIP_ENDPOINTS)[number];

// ─── 9b. Katalog kurir dari DB (mig 3760) ───────────────────────────────────
//
// Melengkapi — BUKAN menggantikan — `BITESHIP_COURIERS` di atas. Konstanta itu
// tetap jadi label cadangan yang selalu tersedia tanpa jaringan maupun DB
// (dipakai `courierLabel` di jalur tarif); tabel `biteship_couriers` adalah
// katalog SUNGGUHAN hasil sinkronisasi, yang tahu 22 kurir & 85 layanan
// (§17c) berikut `service_type` tiap layanan.

/** Satu layanan milik sebuah kurir — bentuk elemen `biteship_couriers.services`. */
export type BiteshipCourierService = {
  /** `courier_service_code` Biteship, lower. */
  serviceCode: string;
  /** `courier_service_name` Biteship, apa adanya. */
  serviceName: string;
  /**
   * `service_type` Biteship, lower ("" bila tak dikirim). Inilah yang
   * menentukan apakah layanan ini akan pernah muncul di hasil cek ongkir —
   * lihat `isCacheableServiceType`.
   */
  serviceType: string;
};

/** Satu baris `biteship_couriers` (mig 3760). */
export type BiteshipCourierRow = {
  courier_code: string;
  courier_name: string;
  services: BiteshipCourierService[];
};

/** Bentuk MENTAH satu entri `couriers[]` dari `GET /v1/couriers`. */
export type BiteshipCourierResponseItem = {
  courier_code?: unknown;
  courier_name?: unknown;
  courier_service_code?: unknown;
  courier_service_name?: unknown;
  service_type?: unknown;
};

/**
 * Kode kurir yang aman dipakai sebagai identitas DAN sebagai nilai di dalam
 * filter PostgREST.
 *
 * Bukan kerapian: route sync menandai kurir yang HILANG dari katalog dengan
 * `.not("courier_code", "in", "(a,b,c)")`, dan daftar itu disusun dari nilai
 * yang datang dari pihak luar. Satu koma/kurung/kutip di dalam sebuah kode
 * akan mengubah arti filternya — dan akibat terburuknya bukan error melainkan
 * kurir yang MASIH ada ikut ditandai tidak tersedia. Karena itu kode yang tak
 * memenuhi pola dibuang di sini, di satu tempat, sebelum menyentuh apa pun.
 */
const COURIER_CODE_PATTERN = /^[a-z0-9_-]{1,32}$/;

/**
 * `couriers[]` mentah → satu baris per KURIR, layanannya dikelompokkan.
 *
 * Biteship memulangkan daftar DATAR: satu baris per (kurir × layanan) — 85
 * baris untuk 22 kurir pada probe 2026-08-17. Yang kita simpan satu baris per
 * kurir, karena toggle admin memang per-kurir (keputusan §14) dan layanan cuma
 * ditampilkan.
 *
 * Dibuang: entri tanpa `courier_code`, kode yang tak memenuhi
 * `COURIER_CODE_PATTERN`, dan layanan duplikat (kode layanan sama muncul dua
 * kali → yang pertama menang). Kurir tanpa satu pun layanan yang bisa dibaca
 * TETAP disimpan — ia ada di katalog, dan menghilangkannya membuat layar tak
 * bisa menjelaskan kode yang mungkin sudah diaktifkan toko.
 *
 * Urutan deterministik (kurir lalu layanan, keduanya menurut kode): isi
 * `services` masuk ke jsonb dan dibandingkan di tes; urutan yang mengikuti
 * balasan API membuat dua sinkronisasi identik menghasilkan dua baris berbeda.
 */
export function parseCourierCatalog(
  couriers: ReadonlyArray<BiteshipCourierResponseItem> | null | undefined
): BiteshipCourierRow[] {
  if (!Array.isArray(couriers)) return [];

  const byCode = new Map<string, BiteshipCourierRow>();
  const seenService = new Map<string, Set<string>>();

  for (const item of couriers) {
    if (!item || typeof item !== "object") continue;
    const code = str(item.courier_code).toLowerCase();
    if (!COURIER_CODE_PATTERN.test(code)) continue;

    let row = byCode.get(code);
    if (!row) {
      row = { courier_code: code, courier_name: "", services: [] };
      byCode.set(code, row);
      seenService.set(code, new Set());
    }

    // Nama kurir: yang pertama non-kosong menang. Baris berikutnya untuk kurir
    // yang sama membawa nama yang sama, tapi kalau salah satunya kosong kita
    // tak mau menimpanya jadi kosong.
    const name = str(item.courier_name);
    if (row.courier_name === "" && name !== "") row.courier_name = name;

    const serviceCode = str(item.courier_service_code).toLowerCase();
    if (serviceCode === "") continue;
    const seen = seenService.get(code)!;
    if (seen.has(serviceCode)) continue;
    seen.add(serviceCode);

    row.services.push({
      serviceCode,
      serviceName: str(item.courier_service_name),
      serviceType: str(item.service_type).toLowerCase(),
    });
  }

  const rows = Array.from(byCode.values());
  for (const row of rows) {
    row.services.sort((a, b) => a.serviceCode.localeCompare(b.serviceCode));
    // Nama kosong dari API → jatuh ke label konstanta / kode uppercase, supaya
    // layar tak pernah menampilkan baris tanpa nama.
    if (row.courier_name === "") row.courier_name = courierLabel(row.courier_code);
  }
  rows.sort((a, b) => a.courier_code.localeCompare(b.courier_code));
  return rows;
}

// ─── 9c. Katalog kurir untuk LAYAR (Fase 5b) ────────────────────────────────

/** Satu kurir sebagaimana dibaca layar setelan — bentuk camelCase. */
export type CourierCatalogEntry = {
  /** Kode Biteship (lower). Inilah yang disimpan di `shipping_couriers`. */
  code: string;
  /** Nama tampilan; tak pernah kosong (jatuh ke `courierLabel`). */
  name: string;
  services: BiteshipCourierService[];
  /** `false` = tidak lagi ada di katalog Biteship (baris sengaja tak dihapus). */
  isAvailable: boolean;
  /** ISO `synced_at`; "" bila tak terbaca. */
  syncedAt: string;
  /**
   * `biteship_couriers.icon_url` (mig 3890) — logo DEFAULT tingkat platform,
   * berlaku untuk semua toko. `null` = belum pernah diunggah. Ditimpa per-toko
   * oleh `expeditions.icon_url`; aturan lengkapnya di `expeditionLogo.ts`.
   */
  iconUrl: string | null;
};

/**
 * Baris `biteship_couriers` mentah → entri layar, dengan koersi DEFENSIF.
 *
 * `services` bertipe jsonb: isinya ditulis route sync hari ini, tapi kolom
 * jsonb juga bisa disunting tangan lewat SQL dan bentuknya bisa berubah bila
 * Biteship mengubah balasannya. Satu baris rusak tidak boleh menjatuhkan
 * SELURUH layar setelan ongkir — idiom `mapPlatformPricingRow` (mig 3510):
 * baca tak pernah melempar, jatuh ke bawaan per-field.
 *
 * Baris tanpa `courier_code` dipulangkan `null` (tak ada yang bisa disimpan
 * maupun ditampilkan tanpa kode), bukan entri kosong yang jadi tombol hantu.
 */
export function mapCourierCatalogRow(row: unknown): CourierCatalogEntry | null {
  if (!row || typeof row !== "object") return null;
  const r = row as Record<string, unknown>;
  const code = str(r.courier_code).toLowerCase();
  if (code === "") return null;

  const services: BiteshipCourierService[] = [];
  if (Array.isArray(r.services)) {
    for (const raw of r.services) {
      if (!raw || typeof raw !== "object") continue;
      const s = raw as Record<string, unknown>;
      const serviceCode = str(s.serviceCode).toLowerCase();
      if (serviceCode === "") continue;
      services.push({
        serviceCode,
        serviceName: str(s.serviceName),
        serviceType: str(s.serviceType).toLowerCase(),
      });
    }
  }

  return {
    code,
    name: str(r.courier_name) || courierLabel(code),
    services,
    // Hanya `false` eksplisit yang mematikan. Kolom hilang / tak terbaca →
    // dianggap TERSEDIA: menandai kurir sehat sebagai "sudah tidak ada" jauh
    // lebih merusak daripada kebalikannya (admin mencabutnya karena percaya
    // peringatan yang salah).
    isAvailable: r.is_available !== false,
    syncedAt: str(r.synced_at),
    // Sengaja BUKAN `str()`: helper itu ikut menerima angka (`42` → `"42"`),
    // dan sebuah URL yang datang sebagai angka bukan URL — ia akan dipasang
    // apa adanya lalu tampil sebagai gambar rusak. Kolomnya baru (mig 3890)
    // dan bisa berisi apa saja lewat SQL tangan, jadi hanya string yang
    // diterima; kosong/spasi → null supaya pemanggil cukup satu pemeriksaan.
    iconUrl:
      typeof r.icon_url === "string" ? r.icon_url.trim() || null : null,
  };
}

/** Banyak baris → entri layar, yang rusak dibuang, urut kode. */
export function mapCourierCatalogRows(rows: unknown): CourierCatalogEntry[] {
  if (!Array.isArray(rows)) return [];
  const out: CourierCatalogEntry[] = [];
  for (const row of rows) {
    const entry = mapCourierCatalogRow(row);
    if (entry) out.push(entry);
  }
  out.sort((a, b) => a.code.localeCompare(b.code));
  return out;
}

/**
 * `true` bila kurir ini PUNYA setidaknya satu layanan yang bisa muncul di hasil
 * cek ongkir — yaitu yang lolos `isCacheableServiceType`.
 *
 * Ini SATU-SATUNYA definisi "kurir tidak didukung", dan ia DIHITUNG dari
 * katalog, tak pernah didaftar tangan. Per katalog 2026-08-18 kebetulan ada
 * enam yang tidak didukung (gojek, grab, deliveree, borzo, lalamove,
 * dash_express) — tapi daftar itu MILIK BITESHIP dan berubah tanpa memberi
 * tahu siapa pun; menuliskannya sebagai konstanta berarti layar akan mengunci
 * kurir yang sudah sehat, atau membuka kurir yang tarifnya tak akan pernah
 * muncul. Turunkan selalu dari `services`.
 *
 * ⚠️ Kurir CAMPURAN tetap didukung. `tiki`, `anteraja`, `sap`, `rpx`, `paxel`,
 * `sentralcargo` punya layanan same-day DAN reguler; yang tersaring hanya
 * layanan same-day-nya, sisanya muncul normal (§17b: TIKI Reguler Rp 14.000).
 * Karena itu syaratnya `some`, bukan `!every` atas layanan same-day — dan
 * `tiki`/`anteraja` bahkan ada di daftar default `shipping_couriers`, jadi
 * salah di sini langsung mematikan dua kurir bawaan.
 *
 * **Daftar layanan KOSONG → `true` (didukung).** Kosong berarti "tidak tahu",
 * bukan "semuanya same-day": bisa karena katalog belum pernah disinkronkan,
 * karena Biteship tak mengirim layanannya, atau karena barisnya disunting
 * tangan. Arah gagal yang benar sama dengan `isCacheableServiceType`: jangan
 * mengunci sesuatu yang belum terbukti bermasalah.
 */
export function courierSupportsRates(
  services: ReadonlyArray<BiteshipCourierService> | null | undefined
): boolean {
  if (!Array.isArray(services) || services.length === 0) return true;
  return services.some((s) => isCacheableServiceType(s?.serviceType));
}

/**
 * Label Indonesia untuk `service_type` yang tak layak-cache.
 *
 * ⚠️ WAJIB memuat SETIAP nilai `UNSUPPORTED_SERVICE_TYPES` — dikunci tes. Kalau
 * daftar itu bertambah dan peta ini tidak, `courierUnsupportedReason` akan
 * melabeli jenis baru dengan nama jenis LAIN, dan kalimat itu satu-satunya yang
 * mencegah admin menyimpulkan "fiturnya rusak". Jenis yang tak dikenal karena
 * itu jatuh ke kalimat generik, BUKAN ke label yang kebetulan tersedia.
 */
const UNSUPPORTED_TYPE_LABEL: Record<string, string> = {
  instant: "instant",
  same_day: "same-day",
};

/**
 * Alasan singkat kenapa sebuah kurir tak bisa dipilih — "" bila ia didukung.
 *
 * Teksnya di sini, bukan di JSX, karena kalimat ini adalah SATU-SATUNYA hal
 * yang mencegah admin menyimpulkan "fiturnya rusak" saat ia menyalakan Gojek
 * lalu tak melihat satu tarif pun.
 *
 * Labelnya DITURUNKAN dari jenis layanan yang benar-benar ada pada kurir itu,
 * bukan dari terner dua-cabang: terner yang jatuh ke "same-day" untuk apa pun
 * yang tak dikenal akan memberi admin alasan yang SALAH begitu Biteship menambah
 * jenis layanan baru — dan salahnya senyap, karena kalimatnya tetap terbaca
 * masuk akal.
 */
export function courierUnsupportedReason(
  services: ReadonlyArray<BiteshipCourierService> | null | undefined
): string {
  if (courierSupportsRates(services)) return "";
  const labels = Array.from(
    new Set(
      (services ?? [])
        .map((s) => UNSUPPORTED_TYPE_LABEL[str(s?.serviceType).toLowerCase()])
        .filter((label): label is string => Boolean(label))
    )
  );
  if (labels.length === 0) {
    // Seluruh layanannya tak layak-cache tapi tak satu pun jenisnya kita kenali
    // namanya. Sebut akibatnya saja — mengarang nama jenis lebih buruk daripada
    // tidak menyebutnya.
    return "Layanannya tidak ditagih per kg, jadi tarifnya belum bisa dihitung otomatis.";
  }
  return `Hanya melayani ${labels.join(" & ")} — tarifnya dihitung per jarak, bukan per kg, jadi belum didukung.`;
}

/**
 * Stempel `synced_at` TERBARU di seluruh katalog — "" bila tak ada yang terbaca.
 *
 * Dibandingkan lewat `Date.parse`, BUKAN urutan huruf. Keduanya kebetulan sama
 * hari ini (satu kolom `timestamptz`, satu kali sync, format seragam), tapi
 * perbandingan huruf atas timestamp membalik hasilnya begitu ada dua offset
 * berbeda yang menyatakan waktu yang sama (`…T09:00:00+07:00` vs `…T02:00:00Z`)
 * — kelas bug yang sudah dikunci tes regresi di tempat lain di repo ini. Angka
 * ini yang dibaca admin sebelum memutuskan perlu sync atau tidak.
 */
export function latestCourierSyncedAt(
  catalog: ReadonlyArray<{ syncedAt?: string | null }> | null | undefined
): string {
  let best = "";
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const entry of catalog ?? []) {
    const iso = str(entry?.syncedAt);
    if (iso === "") continue;
    const ms = Date.parse(iso);
    // Stempel yang tak bisa diurai dilewati, bukan dipakai apa adanya: satu
    // baris rusak tak boleh mengklaim dirinya yang terbaru.
    if (!Number.isFinite(ms) || ms <= bestMs) continue;
    bestMs = ms;
    best = iso;
  }
  return best;
}

/** Keadaan sebuah kurir di layar setelan. */
export type CourierChoiceStatus =
  /** Ada di katalog, punya layanan yang bisa muncul di hasil cek ongkir. */
  | "ok"
  /** Ada di katalog, tapi SELURUH layanannya instant/same-day. */
  | "unsupported"
  /** Ada di katalog tapi `is_available = false` — hilang dari Biteship. */
  | "unavailable"
  /** Menyala di setelan toko tapi tak ada di katalog sama sekali. */
  | "unknown";

/** Satu baris tombol kurir di layar setelan. */
export type CourierChoice = {
  code: string;
  name: string;
  selected: boolean;
  status: CourierChoiceStatus;
  /**
   * `true` = tombolnya tidak boleh ditekan.
   *
   * ⚠️ HANYA berlaku untuk kurir yang sedang MATI. Lihat `buildCourierChoices`.
   */
  locked: boolean;
  /** Penjelasan singkat; "" bila tak ada yang perlu dijelaskan. */
  note: string;
};

/** Urutan tampil: yang bisa dipakai lebih dulu, yang bermasalah di bawah. */
const COURIER_STATUS_ORDER: Record<CourierChoiceStatus, number> = {
  ok: 0,
  unsupported: 1,
  unavailable: 2,
  unknown: 3,
};

/**
 * Katalog + daftar kurir yang menyala → daftar tombol yang dirender layar.
 *
 * **Semua kurir yang diketahui ikut dipulangkan** — termasuk yang
 * `is_available = false` dan sedang mati. Layar yang MENYEMBUNYIKAN sebagian
 * memutuskan sendiri di lapisan tampilan (lihat `visibleCourierChoices`), dan
 * itu pembagian yang disengaja: fungsi murni ini memegang gambaran lengkap
 * supaya ketiadaan sebuah kurir selalu bisa DIJELASKAN, tak pernah senyap.
 *
 * ⚠️ Kenapa itu penting, dan bukan kerapian: `biteship_couriers` GLOBAL (mig
 * 3760) sementara tombol "Muat ulang" ada di panel SETIAP toko, dan
 * `syncCourierCatalog` menandai `is_available=false` untuk tiap kode yang tak
 * ikut di balasan Biteship — satu-satunya pengaman adalah balasan KOSONG.
 * Balasan PARSIAL (200 dengan sebagian kurir hilang) saat admin toko A menekan
 * tombol itu karena itu bisa menyingkirkan sebuah kurir dari layar toko B, C, D
 * — dan bila layar cuma menyaringnya, admin toko B tak punya satu pun petunjuk
 * kenapa JNT hilang dari daftarnya. Pemulihannya sepele (tekan "Muat ulang"
 * lagi); yang tak termaafkan adalah tidak tahu ada yang perlu dipulihkan.
 *
 * ⚠️ **KUNCI HANYA MENGUNCI YANG SEDANG MATI** (`locked = !supported &&
 * !selected`). Ini bukan kelonggaran melainkan pagar: kalau tombol dikunci
 * berdasarkan "tidak didukung" saja, kurir yang TERLANJUR menyala — mis. dua
 * kurir campuran di daftar default, atau setelan lama — jadi mustahil
 * dimatikan, sementara kodenya tetap ikut terkirim ke Biteship di setiap
 * permintaan tarif. Mengunci pintu keluar selalu lebih berbahaya daripada
 * mengunci pintu masuk.
 *
 * Kurir yang tak dikenal katalog TIDAK pernah dikunci: "tidak ada di katalog"
 * berarti kita tak tahu apa-apa tentangnya, dan menguncinya akan menjebak
 * setelan yang sudah tersimpan (kasus katalog belum pernah disinkronkan — di
 * situ SELURUH kurir tak dikenal).
 */
export function buildCourierChoices(
  catalog: ReadonlyArray<CourierCatalogEntry> | null | undefined,
  selected: ReadonlyArray<string | null | undefined> | null | undefined
): CourierChoice[] {
  const on = new Set(
    (selected ?? [])
      .map((code) => str(code).toLowerCase())
      .filter((code) => code !== "")
  );

  const out: CourierChoice[] = [];
  const seen = new Set<string>();

  for (const entry of catalog ?? []) {
    const code = str(entry?.code).toLowerCase();
    if (code === "" || seen.has(code)) continue;
    const isOn = on.has(code);
    const supported = courierSupportsRates(entry.services);
    const status: CourierChoiceStatus = !entry.isAvailable
      ? "unavailable"
      : supported
        ? "ok"
        : "unsupported";

    let note = "";
    if (status === "unavailable") {
      note = isOn
        ? "Tidak lagi tersedia di Biteship, tapi masih menyala di toko ini — sebaiknya dimatikan."
        : "Tidak ikut di daftar terakhir dari Biteship. Muat ulang katalog bila menurutmu ia masih ada.";
    } else if (status === "unsupported") {
      note = isOn
        ? `${courierUnsupportedReason(entry.services)} Sedang menyala — matikan supaya tidak ikut ditanyakan tiap cek ongkir.`
        : courierUnsupportedReason(entry.services);
    }

    seen.add(code);
    out.push({
      code,
      name: entry.name || courierLabel(code),
      selected: isOn,
      status,
      locked: !supported && !isOn,
      note,
    });
  }

  // Kode yang menyala tapi tak ada di katalog — mis. katalog belum pernah
  // disinkronkan (daftar default mig 3720 sudah menyala tanpa satu pun baris
  // katalog), atau kolomnya disunting lewat SQL.
  for (const code of on) {
    if (seen.has(code)) continue;
    seen.add(code);
    out.push({
      code,
      name: courierLabel(code),
      selected: true,
      status: "unknown",
      locked: false,
      note: "Belum ada di katalog — muat ulang daftar kurir untuk memastikannya masih dilayani Biteship.",
    });
  }

  out.sort((a, b) => {
    const byStatus =
      COURIER_STATUS_ORDER[a.status] - COURIER_STATUS_ORDER[b.status];
    if (byStatus !== 0) return byStatus;
    return a.name.localeCompare(b.name, "id");
  });
  return out;
}

/**
 * `true` bila baris ini layak disembunyikan dari grid utama: kurir yang sudah
 * hilang dari katalog Biteship DAN tidak dipakai toko ini.
 *
 * Yang MENYALA tak pernah disembunyikan — itu satu-satunya cara mematikannya.
 */
export function isDormantCourierChoice(choice: CourierChoice): boolean {
  return choice.status === "unavailable" && !choice.selected;
}

/**
 * Bagi daftar tombol jadi yang ditampilkan langsung vs yang dilipat.
 *
 * Kurir mati yang tak dipakai memang tak perlu memenuhi layar — barisnya tak
 * pernah dihapus dari DB (mig 3760), jadi tanpa pelipatan daftar itu tumbuh
 * selamanya. Tapi jumlahnya WAJIB disebut: itu bedanya "disembunyikan karena
 * tak relevan" dari "hilang entah kenapa", dan hanya yang pertama yang bisa
 * ditindaklanjuti admin.
 */
export function visibleCourierChoices(
  choices: ReadonlyArray<CourierChoice>,
  showDormant = false
): { visible: CourierChoice[]; dormantCount: number } {
  const dormant = choices.filter(isDormantCourierChoice);
  return {
    visible: showDormant
      ? [...choices]
      : choices.filter((c) => !isDormantCourierChoice(c)),
    dormantCount: dormant.length,
  };
}

// ─── 9d. Toleransi berat: kalimat peringatan (Fase 5b) ──────────────────────

/**
 * Kalimat yang WAJIB tampil di bawah kolom toleransi berat.
 *
 * Angka contohnya **DITURUNKAN dari `weightBucketGram`**, fungsi yang sama yang
 * dipakai route saat menanyakan tarif — bukan diketik tangan. Kalimat yang
 * ditulis tangan akan menyimpang begitu aturan pembulatan disentuh, dan
 * menyimpangnya senyap: admin membaca contoh yang salah lalu memilih angka
 * berdasarkan itu.
 *
 * Kenapa peringatan ini tidak boleh jadi tooltip: toleransi TIDAK membuat
 * kurir menagih lebih murah. Kurir Indonesia benar-benar menagih 1.250 g
 * sebagai 2 kg; yang berubah hanya angka yang KITA tampilkan, dan selisihnya
 * ditanggung toko. Itu keputusan uang, dan keputusan uang harus terbaca tanpa
 * hover — di HP tak ada hover sama sekali.
 */
export function weightToleranceWarning(toleranceGram: number): string {
  const tol = clampWeightToleranceGram(toleranceGram);
  if (tol === 0) {
    return "Tanpa toleransi: berat dibulatkan ke atas per 1 kg, persis seperti yang ditagih kurir.";
  }
  // Contoh: berat tepat 1 kg + toleransi → jatuh ke bucket 1 kg bagi kita,
  // sementara kurir sudah menghitungnya 2 kg.
  const sample = 1000 + tol;
  const ours = weightBucketGram(sample, tol) / 1000;
  const courier = Math.ceil(sample / 1000);
  return (
    `Toleransi ${tol} g → paket ${sample.toLocaleString("id-ID")} g dihitung ` +
    `${ours} kg, padahal kurir menagih ${courier} kg. Selisihnya ditanggung toko.`
  );
}

// ─── 10. Tampilan tarif (Fase 4) ────────────────────────────────────────────

/**
 * Satuan durasi Biteship → Bahasa Indonesia.
 *
 * ⚠️ HANYA menerjemahkan satuan yang DIDAHULUI ANGKA. Itu bukan kerapian
 * melainkan pagar: `duration` adalah string bebas dari pihak luar, dan
 * penggantian kata "day" tanpa syarat mengubah **"same day"** jadi
 * **"same hari"** — kalimat rusak yang justru muncul pada layanan instan/
 * same-day yang paling sering dipakai toko.
 *
 * Frasa yang memang berdiri sendiri ditangani terpisah (`DURATION_PHRASES`),
 * dan apa pun yang tak dikenali dipulangkan APA ADANYA. Arah gagalnya selalu
 * sama: teks Inggris yang masih terbaca, bukan teks Indonesia yang salah.
 */
const DURATION_UNITS: ReadonlyArray<readonly [RegExp, string]> = [
  [/(\d)\s*(?:days?)\b/gi, "$1 hari"],
  [/(\d)\s*(?:hours?|hrs?)\b/gi, "$1 jam"],
  [/(\d)\s*(?:minutes?|mins?)\b/gi, "$1 menit"],
  [/(\d)\s*(?:weeks?)\b/gi, "$1 minggu"],
  [/(\d)\s*(?:months?)\b/gi, "$1 bulan"],
] as const;

/** Frasa utuh tanpa angka — diterapkan SETELAH satuan ber-angka. */
const DURATION_PHRASES: ReadonlyArray<readonly [RegExp, string]> = [
  [/\bsame\s*day\b/gi, "hari yang sama"],
  [/\bnext\s*day\b/gi, "hari berikutnya"],
  [/\binstant\b/gi, "instan"],
] as const;

/**
 * `ShippingRate.duration` mentah ("1 - 2 days") → teks Indonesia ("1 - 2 hari").
 *
 * Dipisah dari komponen (bukan diformat inline di JSX) karena teks yang sama
 * muncul di TIGA permukaan — daftar tarif pembeli, daftar tarif admin, dan
 * catatan yang tersimpan ke `notes` — dan tiga salinan aturan penerjemahan
 * pasti menyimpang. Kosong tetap kosong: pemanggil menyembunyikan barisnya.
 */
export function formatDuration(raw: string | null | undefined): string {
  let out = str(raw);
  if (out === "") return "";
  for (const [pattern, replacement] of DURATION_UNITS) {
    out = out.replace(pattern, replacement);
  }
  for (const [pattern, replacement] of DURATION_PHRASES) {
    out = out.replace(pattern, replacement);
  }
  return out.replace(/\s+/g, " ").trim();
}

/** Identitas satu tarif dalam satu daftar: `"<kurir>:<layanan>"`. */
export function rateKey(
  rate: Pick<ShippingRate, "courierCode" | "serviceCode">
): string {
  return `${str(rate.courierCode).toLowerCase()}:${str(
    rate.serviceCode
  ).toLowerCase()}`;
}

/** "JNE · REG" — nama kurir & layanan, jatuh ke kodenya bila nama kosong. */
export function rateLabel(rate: ShippingRate): string {
  const courier = rate.courierName || courierLabel(rate.courierCode);
  const service = rate.serviceName || rate.serviceCode.toUpperCase();
  return [courier, service].filter((s) => s !== "").join(" · ");
}

/**
 * Naikkan tarif milik SATU kode kurir ke urutan teratas, sisanya tetap.
 *
 * Dipakai saat ekspedisi yang sudah dipilih punya `biteship_courier_code`
 * (mig 3720): admin yang sudah menetapkan "JNE" tak seharusnya menyusuri
 * daftar untuk menemukan tarif JNE-nya.
 *
 * **Stabil di kedua partisi** — urutan masuk (harga naik, hasil
 * `normalizeRates`) dipertahankan di dalam masing-masing kelompok, dan input
 * tidak dimutasi. Kode kosong/tak dikenal = daftar dipulangkan apa adanya,
 * BUKAN daftar yang disaring: menyembunyikan tarif kurir lain akan membuat
 * satu kode kurir yang salah ketik terlihat seperti "tak ada tarif".
 */
export function sortRatesForCourier(
  rates: ReadonlyArray<ShippingRate>,
  preferredCode: string | null | undefined
): ShippingRate[] {
  const code = str(preferredCode).toLowerCase();
  if (code === "") return [...rates];
  const preferred: ShippingRate[] = [];
  const rest: ShippingRate[] = [];
  for (const rate of rates) {
    (rate.courierCode.toLowerCase() === code ? preferred : rest).push(rate);
  }
  return [...preferred, ...rest];
}

/**
 * Awalan baris catatan tarif. Konstanta karena dipakai DUA arah: menulis
 * barisnya dan MENGENALI baris lama untuk diganti. Kalau keduanya menulis
 * literal sendiri, mengganti pilihan akan menumpuk baris alih-alih menimpanya.
 */
export const RATE_NOTE_PREFIX = "Cek ongkir:";

/**
 * Satu baris catatan jejak tarif — "Cek ongkir: JNE · REG · Rp20.000 · 1 - 2 hari".
 *
 * Gunanya bukan kosmetik: ongkir yang ditagihkan adalah angka yang DIKETIK
 * admin, jadi saat tagihan kurir tak cocok, satu-satunya cara menelusuri
 * asalnya adalah catatan ini. Harga ikut ditulis apa adanya supaya selisih
 * terhadap nominal yang akhirnya disimpan terlihat.
 */
export function rateNoteText(rate: ShippingRate): string {
  const duration = formatDuration(rate.duration);
  return [
    `${RATE_NOTE_PREFIX} ${rateLabel(rate)}`,
    `Rp${Math.round(rate.price).toLocaleString("id-ID")}`,
    duration,
  ]
    .filter((part) => part !== "")
    .join(" · ");
}

/**
 * Sisipkan/ganti baris jejak tarif di dalam `notes`.
 *
 * Baris lama ber-awalan `RATE_NOTE_PREFIX` DIBUANG lebih dulu — mengganti
 * pilihan tarif tiga kali tidak boleh meninggalkan tiga baris yang saling
 * bertentangan di catatan yang dibaca petugas gudang. `rate` null = hanya
 * membuang (pilihan dibatalkan). Catatan manusia di baris lain tak disentuh.
 */
export function withRateNote(
  notes: string | null | undefined,
  rate: ShippingRate | null
): string {
  return withRateNoteText(notes, rate ? rateNoteText(rate) : null);
}

/**
 * Bentuk umum `withRateNote`: menerima BARIS jadi, bukan `ShippingRate`.
 *
 * Ada karena sejak mig 3990 ada sumber tarif kedua — ekspedisi manual milik
 * toko — yang bukan `ShippingRate` dan tak bisa dipaksa jadi bentuk itu.
 * Keduanya WAJIB memakai awalan yang sama: baris lama dikenali dari awalannya,
 * jadi dua awalan berbeda berarti berganti dari tarif kurir ke tarif manual
 * meninggalkan DUA baris yang saling bertentangan di catatan yang dibaca
 * petugas gudang.
 */
export function withRateNoteText(
  notes: string | null | undefined,
  text: string | null
): string {
  const kept = str(notes)
    .split("\n")
    .filter((line) => !line.trim().startsWith(RATE_NOTE_PREFIX));
  if (text && text.trim() !== "") kept.push(text.trim());
  return kept
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// ─── 12. Kunci API — pembersihan & pemilihan sumber ─────────────────────────
//
// MURNI, dan sengaja hidup di sini alih-alih di `server/biteshipClient.ts`:
// modul itu ber-`import "server-only"` sehingga mustahil diimpor Vitest, dan
// aturan yang tak bisa diuji adalah aturan yang diam-diam menyimpang. Dua
// fungsi di bawah adalah SELURUH logika "kunci mana yang dipakai"; pemanggil
// server tinggal menyediakan bahannya.

/** Dari mana kunci Biteship yang dipakai proses ini berasal. */
export type BiteshipKeySource = "db" | "env" | null;

/**
 * Bersihkan kunci API dari sisa salin-tempel.
 *
 * Karakter non-ASCII-printable DIBUANG, bukan ditolak: satu newline yang ikut
 * tersalin dari panel hosting membuat header HTTP tak sah dan `fetch` gagal
 * dengan "String contains non ISO-8859-1 code point" — pesan yang sama sekali
 * tidak menunjuk ke kuncinya. Membuang lebih menolong daripada menolak, karena
 * kunci yang "terlihat benar di layar" memang selalu benar isinya.
 */
export function sanitizeBiteshipKey(raw: string | null | undefined): string {
  return (raw ?? "").replace(/[^\x20-\x7E]/g, "").trim();
}

/**
 * Pilih kunci yang dipakai: **DB dulu, `process.env` sebagai cadangan.**
 *
 * Urutannya bukan selera. Nilai DB dibaca per-QUERY sehingga SEMUA proses
 * melihat baris yang sama, sementara `process.env` dibaca per-PROSES dan
 * terbukti bisa timpang antar proses pada satu deployment (mig 4000). Kalau
 * env didahulukan, proses yang env-nya "kebetulan terisi nilai lama" akan terus
 * memakai nilai itu dan perbaikan lewat panel tidak pernah menyeluruh — persis
 * kelas bug yang pemindahan ini hendak akhiri.
 *
 * Cadangan env TETAP ADA supaya deployment yang env-nya sehat tidak perlu
 * mengisi apa pun untuk terus jalan, dan supaya mengosongkan nilai di panel
 * adalah jalan mundur yang sah, bukan pemadaman.
 */
export function pickBiteshipKey(
  dbValue: string | null | undefined,
  envValue: string | null | undefined
): { key: string; source: BiteshipKeySource } {
  const fromDb = sanitizeBiteshipKey(dbValue);
  if (fromDb !== "") return { key: fromDb, source: "db" };
  const fromEnv = sanitizeBiteshipKey(envValue);
  if (fromEnv !== "") return { key: fromEnv, source: "env" };
  return { key: "", source: null };
}

/**
 * Status kunci Biteship seperti yang boleh dilihat panel platform.
 *
 * ⛔ TANPA NILAI KUNCINYA, dan itu bukan kelalaian: RPC-nya memang tidak
 * pernah memulangkan nilai, sehingga rahasia hidup tak pernah melintas ke
 * browser walau layarnya cuma dibuka pemilik. `fingerprint` (8 hex pertama
 * md5) ada untuk MEMBANDINGKAN dengan kunci di dashboard Biteship tanpa
 * memindahkan nilainya.
 */
export type BiteshipKeyStatus = {
  configured: boolean;
  length: number;
  fingerprint: string | null;
  updatedAt: string | null;
};

/** Baris jsonb `platform_biteship_key_status()` → bentuk klien. Longgar: ia
 *  datang dari DB lewat jaringan, jadi tiap field dinormalkan sendiri. */
export function mapBiteshipKeyStatus(raw: unknown): BiteshipKeyStatus {
  const row = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const fingerprint = str(row.fingerprint);
  const updatedAt = str(row.updatedAt);
  const length = num(row.length);
  return {
    configured: row.configured === true,
    length: length === null || length < 0 ? 0 : Math.round(length),
    fingerprint: fingerprint === "" ? null : fingerprint,
    updatedAt: updatedAt === "" ? null : updatedAt,
  };
}
